import React, { useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import { Group, Grouping, Section } from '../../../contexts/MetadataContext';
import MemoizedSection from './MemoizedSection';
import FormAccordion from '../../../components/common/FormAccordion';

interface MemoizedGroupProps {
  groupings: Grouping[];
  group: Group;
  groupIndex: number;
  onCustomControlLoaded: (groupIndex: number, fieldKey: string) => void;
  sectionRefs: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
  v2?: boolean;
}

const MemoizedGroup: React.FC<MemoizedGroupProps> = React.memo(
  ({
    groupings,
    group,
    groupIndex,
    onCustomControlLoaded,
    sectionRefs,
    v2,
  }) => {
    const handleCustomControlLoaded = useCallback(
      (fieldKey: string) => {
        onCustomControlLoaded(groupIndex, fieldKey);
      },
      [groupIndex, onCustomControlLoaded],
    );

    const memoizedSections = useMemo(() => {
      return group.sections.map((section: Section, sectionIndex: number) => (
        <MemoizedSection
          key={`${group.name}_${section.name}`}
          totalSections={group.sections.length}
          groupings={groupings}
          section={section}
          groupIndex={groupIndex}
          sectionIndex={sectionIndex}
          onCustomControlLoaded={handleCustomControlLoaded}
          sectionRefs={sectionRefs}
          groupName={group.name}
          v2={v2}
        />
      ));
    }, [
      group.sections,
      groupings,
      groupIndex,
      handleCustomControlLoaded,
      sectionRefs,
      group.name,
      v2,
    ]);

    return v2 ? (
      <Box sx={{ mb: 4, mt: 5, ml: 4, mr: 4 }}>
        <FormAccordion title={group.name} defaultExpanded={true} padding={0}>
          {memoizedSections}
        </FormAccordion>
      </Box>
    ) : (
      <Box sx={{ mb: 6 }}>
        <Box
          sx={{
            width: '100%',
            backgroundColor: 'grey.50',
            fontWeight: 'bold',
            fontSize: '1.4rem',
            pl: 4,
            py: 1,
          }}
        >
          {group.name}
        </Box>
        {memoizedSections}
      </Box>
    );
  },
  (prevProps, nextProps) =>
    prevProps.group === nextProps.group &&
    prevProps.groupings === nextProps.groupings &&
    prevProps.groupIndex === nextProps.groupIndex &&
    prevProps.onCustomControlLoaded === nextProps.onCustomControlLoaded &&
    prevProps.sectionRefs === nextProps.sectionRefs &&
    prevProps.v2 === nextProps.v2,
);

MemoizedGroup.displayName = 'MemoizedGroup';

export default MemoizedGroup;
